import { IBookingPurchaseOptionItem } from "@/api_framework/api_modals/Bookings";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import previous from "@/assets/previous.svg";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";

export interface IServiceAddOnCard extends IBookingPurchaseOptionItem {
  image_url: string;
  isAdded: boolean;
  previously_added:boolean;
}

interface IServiceAddOnsSectionProps {
  isLoading: boolean;
  addOns: IServiceAddOnCard[];
  formatAmount: (amount: number) => string;
  onToggleAddOn: (addOnId: string) => void;
  showDiscount?: boolean;
  addOnCouponMap?: Record<string, string>;
  onSelectAddOnCoupon?: (addOnId: string, couponId: string | null, couponName?: string) => void;
}

const skeletonClass = "animate-pulse rounded bg-[#E9ECF2]";

const ServiceAddOnsSection: React.FC<IServiceAddOnsSectionProps> = ({
  isLoading,
  addOns,
  formatAmount,
  onToggleAddOn,
  showDiscount = false,
  addOnCouponMap = {},
  onSelectAddOnCoupon,
}) => {
  return (
    <section className="px-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-[14px] font-semibold text-[#1F2431]">Service Add Ons</h4>
      </div>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-[10px] border border-[#E2E4EA] p-3">
              <div className="flex items-start gap-3">
                <div className={`${skeletonClass} h-[66px] w-[66px] rounded-xl`} />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className={`${skeletonClass} h-5 w-2/3`} />
                  <div className={`${skeletonClass} h-4 w-full`} />
                  <div className={`${skeletonClass} h-4 w-1/2`} />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-2">
                <div className="space-y-2">
                  <div className={`${skeletonClass} h-5 w-20`} />
                  <div className={`${skeletonClass} h-4 w-14`} />
                </div>
                <div className={`${skeletonClass} h-10 w-[84px] rounded-xl`} />
              </div>
            </div>
          ))}
        </div>
      ) : addOns.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {addOns.map((addon) => {
            const addOnPrice = Number(addon.final_price ?? 0);
            const addOnOriginalPrice = Number(addon.price ?? addOnPrice);
            const quantityAdded = Number(addon.qty ?? (addon.isAdded ? 1 : 0));
            const maxQuantity = Number(addon.max_qty ?? 1);
            const isMaxQtyReached = quantityAdded >= maxQuantity;
            const addOnDiscount =
              addOnOriginalPrice > 0
                ? Math.max(0, Math.round(((addOnOriginalPrice - addOnPrice) / addOnOriginalPrice) * 100))
                : 0;
            const hasAddOnDiscount = addOnDiscount > 0 && addOnOriginalPrice > addOnPrice;

            const applicableCoupons = addon.applicable_coupons ?? [];
            const couponItems = applicableCoupons.map((coupon) => ({
              id: coupon.coupon_id,
              title: coupon.coupon_name,
              state: { coupon_visibility: coupon.coupon_visibility },
            }));
            const selectedCouponId = addOnCouponMap[addon.id] || addon.coupon_id || "";
            const hasCoupons = couponItems.length > 0;

            return (
              <div key={addon.id} className="h-full rounded-[10px] border border-[#E2E4EA] p-[8px] flex flex-col">
                <div className="flex min-h-0 flex-1 items-start gap-3">
                  <img src={addon.image_url} alt={addon.name} className="h-[50px] w-[50px] object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <p className="text-[14px] font-medium text-[#1F2431]">{addon.name}</p>
                        {addon.gift_id && addon.offer_callout && <OfferCalloutBadge callout={addon.offer_callout} />}
                      </div>
                      {addon.previously_added && (
                        <span
                          className="shrink-0 inline-flex items-center gap-1 rounded-[44px] px-2 py-0.5 text-[10px] font-medium text-[#4375FF]"
                          style={{
                            background: "linear-gradient(111.32deg, #F2F4FF 29.47%, #D0DDFF 106.2%)",
                            border: "0.5px solid #8CA9FA",
                          }}
                        >
                          <img src={previous} alt="previous" className="h-3 w-3" />
                          Previously added
                        </span>
                      )}
                    </div>
                    <p className="mt-1 line-clamp-3 text-[12px] text-[#6D6D6D]">{addon.description || "Service add-on"}</p>
                  </div>
                </div>

                <div className="mt-4 flex items-end justify-between gap-2">
                  <div className="flex items-center gap-1 text-[14px]">
                    <span className="font-medium text-[#1F2431]">{formatAmount(addOnPrice)}</span>
                    {hasAddOnDiscount && (
                      <>
                        <span className="text-[#8A92A2] text-[12px] font-medium line-through">{formatAmount(addOnOriginalPrice)}</span>
                        <span className="font-medium text-[#239A3D] text-[12px]">{addOnDiscount}%</span>
                      </>
                    )}
                  </div>

                  {addon.isAdded ? (
                    <div className="flex h-[30px] min-w-[75px] items-center rounded-[6px] border border-[#E2E2E2]">
                      <button type="button" className="p-1 text-[#2A3140]" onClick={() => onToggleAddOn(addon.id)}>
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <span className="px-3 text-[12px] text-[#222222]">{quantityAdded}</span>
                      <button
                        type="button"
                        className={`p-1 ${isMaxQtyReached ? "cursor-not-allowed text-[#8F8F8F]" : "text-[#2A3140]"}`}
                        disabled={isMaxQtyReached}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onToggleAddOn(addon.id)}
                      className="flex h-[30px] min-w-[75px] items-center justify-center rounded-[6px] border border-[#D9DDE5] bg-white p-[8px] text-[12px] text-[#242933]"
                    >
                      Add
                    </button>
                  )}
                </div>

                {showDiscount && (
                  <div className="mt-3">
                    <p className="mb-1 text-[12px] font-medium text-[#6D6D6D]">Discount</p>
                    <SearchableSelectMenu
                      register={{} as any}
                      fieldError={undefined}
                      errorMessages={[]}
                      placeholder={hasCoupons ? "Select discount" : "No discount"}
                      showDropdownIcon={true}
                      withReset={hasCoupons}
                      disabled={!hasCoupons}
                      listZIndex="z-30"
                      listBoxClassName="min-w-full w-max max-w-[320px]"
                      defaultSelectId={selectedCouponId}
                      selectItems={couponItems}
                      className={`text-[12px] font-medium text-[#3B4250] ${!hasCoupons ? "!bg-transparent" : ""}`}
                      inputContainerClassName={`h-[36px] flex items-center border-[#E2E2E2] rounded-[8px] ${hasCoupons ? "bg-white" : "bg-gray-200"}`}
                      containerClassName="w-full"
                      onSelectItem={(item) => onSelectAddOnCoupon?.(addon.id, item?.id ?? null, item?.title)}
                      filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                      onEnterPress={(typedValue) => {
                        if (!typedValue) return;
                        const exactMatch = applicableCoupons.find(
                          (c) => c.coupon_name?.toLowerCase() === typedValue.toLowerCase(),
                        );
                        if (exactMatch) {
                          if (exactMatch.coupon_id === selectedCouponId) return;
                          onSelectAddOnCoupon?.(addon.id, exactMatch.coupon_id, exactMatch.coupon_name);
                        } else {
                          toast.error("Coupon not applicable");
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-[#E6E8ED] p-4 text-sm text-[#6B7280]">No add-ons available</div>
      )}
    </section>
  );
};

export default ServiceAddOnsSection;
